/* eslint-disable no-async-promise-executor */

import {
    signInWithEmailAndPassword,
    onAuthStateChanged,
    OAuthProvider,
    signInWithRedirect,
    getRedirectResult,
} from 'firebase/auth';
import { auth, callable } from '$lib/firebase/firebase';
import { writable } from 'svelte/store';
import { showLoader, showAuthModal } from '@repo/stores/Actions';
import { goto } from '$app/navigation';
import { classJoinCode } from '@repo/stores/UserStore';
import { postHogCapture, posthog } from '@repo/config/postHogHelper';
import { TARGET_APPS } from '@repo/config/Global.js';
import { getUserApps as sharedGetUserApps, revokeTokensWithTimeout } from '@repo/firebase';
import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/sveltekit';

export const currentUser = writable(null);

// Stash a `returnTo` URL from the query string into sessionStorage so it
// survives the login flow + OAuth redirects. Consumed by redirectToApp /
// the select-app picker to send the user back to their original deep link.
const RETURN_TO_KEY = 'bzw_auth_returnTo';

if (typeof window !== 'undefined') {
    try {
        const url = new URL(window.location.href);
        const incoming = url.searchParams.get('returnTo');
        if (incoming) {
            // Only honour absolute http(s) URLs. Origin matching against
            // APP_URLS happens in consumeReturnTo at redirect time.
            try {
                const parsed = new URL(incoming);
                if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
                    sessionStorage.setItem(RETURN_TO_KEY, parsed.toString());
                }
            } catch { /* ignore malformed returnTo */ }
            url.searchParams.delete('returnTo');
            window.history.replaceState({}, '', url.toString());
        }
    } catch { /* ignore */ }
}

// A TAP access code the user checked on /tap-join before choosing "I already
// have a BIOZONE account". Survives the /login round-trip (sessionStorage is
// per-tab, which is exactly the scope we want) and is consumed by /tap-join
// once a session exists — see select-app's pending-code redirect.
const PENDING_TAP_CODE_KEY = 'bzw_tap_pending_code';

function setPendingTapCode(code) {
    if (typeof window === 'undefined') return;
    try {
        if (code) sessionStorage.setItem(PENDING_TAP_CODE_KEY, code);
        else sessionStorage.removeItem(PENDING_TAP_CODE_KEY);
    } catch { /* ignore */ }
}

function readPendingTapCode() {
    if (typeof window === 'undefined') return null;
    try {
        return sessionStorage.getItem(PENDING_TAP_CODE_KEY);
    } catch {
        return null;
    }
}

function clearPendingTapCode() {
    setPendingTapCode(null);
}

function readReturnTo() {
    if (typeof window === 'undefined') return null;
    try {
        return sessionStorage.getItem(RETURN_TO_KEY);
    } catch {
        return null;
    }
}

function clearReturnTo() {
    if (typeof window === 'undefined') return;
    try { sessionStorage.removeItem(RETURN_TO_KEY); } catch { /* ignore */ }
}

onAuthStateChanged(auth, async (user) => {
    showLoader.set('checking user...');

    // If we arrived here from another app's sign-out redirect, honour it by
    // clearing the local Auth-app session too. Without this, the session
    // cached in IndexedDB on this origin would auto-sign the user back in.
    if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        if (url.searchParams.has('signedOut')) {
            url.searchParams.delete('signedOut');
            window.history.replaceState({}, '', url.toString());
            if (user?.uid) {
                await auth.signOut();
                return; // onAuthStateChanged will fire again with null
            }
        }
    }

    if (user?.uid) {
        currentUser.set(user);
        showAuthModal.set(false);
        showLoader.set(false);

        // If user is on login or root page, redirect to app selector
        if (typeof window !== 'undefined') {
            const path = window.location.pathname;
            if (path === '/' || path === '/login') {
                await goto('/select-app');
            }
        }
    } else {
        currentUser.set(null);
        showAuthModal.set(true);
        showLoader.set(false);
    }
});

async function signInWithEmail(credentials) {
    try {
        const response = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
        const { user } = response;
        const token = await user.getIdToken();
        const decodedToken = jwtDecode(token);
        const changePassword = decodedToken?.changePassword || false;

        if (changePassword) {
            throw { code: 'auth/change-password', message: 'User must change password' };
        }
    } catch (error) {
        console.error(error);
        throw error;
    }
}

/**
 * Which kind of code a surface is allowed to redeem
 */
function expectForApp(targetApp = TARGET_APPS.TAP) {
    return targetApp === TARGET_APPS.TAP ? 'subscription' : 'class';
}

/**
 * Create an account from a sign-up code, then sign the new user in.
 */
async function signUpWithCode(credentials, options = {}) {
    const signUpWithCodeFn = callable('signUpWithCode');
    const res = await signUpWithCodeFn({ ...credentials, expect: expectForApp(options.targetApp) });

    if (res?.data?.created === false) {
        try {
            await loginWithCode(credentials, options);
        } catch (error) {
            if (!auth.currentUser) {
                console.error('[signup] existing account could not be signed in', error);
                throw Object.assign(new Error('An account already exists for that email'), {
                    code: 'signup/account-exists',
                    cause: error,
                });
            }
            throw error;
        }

        return res;
    }

    // Newly created: the sign-up itself consumed the code, so signing in is all
    // that is left to do.
    try {
        await signInWithEmail(credentials);
    } catch (error) {
        console.error('[signup] auto sign-in after sign-up failed', error);
    }

    return res;
}

/**
 * Sign in, then redeem a sign-up code for that account and hand them on to the
 * app the code belongs to.
 */
async function loginWithCode(credentials, { targetApp = TARGET_APPS.TAP } = {}) {
    await signInWithEmail(credentials);

    if (targetApp === TARGET_APPS.TAP) {
        await joinTapWithCode(credentials.code);
        return;
    }

    await joinClassWithCode(credentials.code);
}

function joinClassWithCode(code) {
    return new Promise(async (resolve, reject) => {
        try {
            const joinWithCode = callable('joinWithCode');
            await joinWithCode({ code, expect: 'class' });

            classJoinCode.set(null);

            // After joining a class, redirect to Education app with token
            await redirectToApp(TARGET_APPS.EDUCATION);
            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

/**
 * Redeem a TAP subscription code for the already signed-in user, then hand
 * them to the TAP app with a fresh custom token. `expect: 'subscription'`
 * stops a class join code being consumed on this surface.
 */
async function joinTapWithCode(code) {
    const joinWithCodeFn = callable('joinWithCode');
    await joinWithCodeFn({ code, expect: 'subscription' });

    classJoinCode.set(null);

    await redirectToApp(TARGET_APPS.TAP);
}

async function validateSignUpCode({ code, deviceId }) {
    const validateSignUpCodeFn = callable('validateSignUpCode');
    const res = await validateSignUpCodeFn({ code, deviceId });

    if (res?.data?.success) {
        return res.data;
    }

    throw new Error('Invalid code');
}

/**
 * Attach a TAP access code to the signed-in account (the "I already have a
 * BIOZONE account" path). `redeemCodeForUser` owns the whole redemption —
 * code consumption, tap-db user doc, membership arrays and the `tap` app
 * claim — so after it resolves `redirectToApp(TARGET_APPS.TAP)` can mint a
 * TAP session straight away.
 *
 * @param {string} code
 * @returns {Promise<{ subscriptionId: string|null, subscription: object|null }>}
 */
async function redeemTapCodeForUser(code) {
    const redeemCodeForUser = callable('redeemCodeForUser');
    const { data } = await redeemCodeForUser({ code });
    return {
        subscriptionId: data?.subscriptionId || null,
        subscription: data?.subscription || null,
    };
}

async function resetPasswordEmail(credentials) {
    const sendPasswordResetEmail = callable('sendPasswordResetEmail');
    await sendPasswordResetEmail(credentials.email);
}

/**
 * Sign in with ClassLink OAuth Provider
 */
function signInWithClassLink() {
    console.debug('Starting ClassLink Sign In Process');
    const provider = new OAuthProvider('oidc.classlink');
    provider.addScope('full');
    signInWithProvider(provider);
}

/**
 * Sign in with Clever OAuth Provider
 */
function signInWithClever() {
    console.debug('Starting Clever Sign In Process');
    const provider = new OAuthProvider('oidc.clever');
    signInWithProvider(provider);
}

/**
 * Sign in with a given OAuth provider
 * @param {OAuthProvider} provider
 */
function signInWithProvider(provider) {
    signInWithRedirect(auth, provider);
}

/**
 * Handles the redirect result from an OAuth provider.
 */
async function getRedirectResultOAuthProvider() {
    try {
        showLoader.set('loading...');

        const result = await getRedirectResult(auth);

        if (!result) {
            console.debug('No result from getRedirectResultOAuthProvider');
            return;
        }

        const user = result.user;

        // Link the user account if it's a new SSO user
        const linkUserAccount = callable('linkUserAccount');
        await linkUserAccount(user.uid).catch((error) => {
            // If user entity already exists, this is expected to fail gracefully
            console.debug('linkUserAccount result:', error);
        });

        // Wait for user entity to be created by cloud function
        await new Promise((resolve, reject) => {
            let interval;
            const timeout = setTimeout(() => {
                clearInterval(interval);
                reject(new Error('Operation timed out after 30 seconds'));
            }, 30000);

            interval = setInterval(async () => {
                console.debug('Checking for user entity...');
                if (auth.currentUser) {
                    clearInterval(interval);
                    clearTimeout(timeout);
                    resolve();
                }
            }, 1000);
        });

        // SSO users (Clever / ClassLink) are Education-only — skip the picker.
        await redirectToApp(TARGET_APPS.EDUCATION);
    } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        throw error;
    } finally {
        showLoader.set(false);
    }
}

function changePassword(password, confirmPassword) {
    return new Promise((resolve, reject) => {
        const changePasswordFn = callable('changePassword');
        changePasswordFn({ password, confirmPassword })
            .then((res) => {
                resolve(res);
            })
            .catch(reject);
    });
}

function getCurrentUser() {
    return currentUser;
}

/**
 * Generate a custom token for cross-app authentication.
 * @param {'education' | 'tap' | 'admin'} targetApp
 * @returns {Promise<string>} The custom token
 */
async function generateAppToken(targetApp) {
    const generateAppTokenFn = callable('generateAppToken');
    const { data } = await generateAppTokenFn({ targetApp });
    return data.token;
}

/**
 * Resolve the list of apps the current user can access. Wraps the shared
 * @repo/firebase helper with the auth app's own `auth` + `callable`.
 *
 * @returns {Promise<string[]>} e.g. ['education', 'tap']
 */
async function getUserApps() {
    return sharedGetUserApps(auth.currentUser, callable);
}

const APP_URLS = {
    [TARGET_APPS.EDUCATION]: () => import.meta.env.VITE_EDUCATION_URL,
    [TARGET_APPS.TAP]: () => import.meta.env.VITE_TAP_URL,
    [TARGET_APPS.ADMIN]: () => import.meta.env.VITE_ADMIN_URL,
    [TARGET_APPS.PERSONAL]: () => import.meta.env.VITE_PERSONAL_URL,
};

/**
 * Match a URL's origin against APP_URLS and return the target app key, or
 * null if no app is configured for that origin.
 *
 * @param {string} url
 * @returns {'education' | 'tap' | 'admin' | 'personal' | null}
 */
function inferTargetAppFromUrl(url) {
    if (!url) return null;
    try {
        const parsed = new URL(url);
        for (const [app, resolveUrl] of Object.entries(APP_URLS)) {
            const appUrl = resolveUrl();
            if (!appUrl) continue;
            try {
                if (new URL(appUrl).origin === parsed.origin) return app;
            } catch { /* skip malformed env */ }
        }
    } catch { /* malformed input */ }
    return null;
}

/**
 * Generate a custom token and redirect the user to the target app. If a
 * `returnTo` URL was stashed at module load and its origin matches the target
 * app, the user is sent to that exact URL (with `?token=` appended) so deep
 * links round-trip. Otherwise we redirect to the app's root.
 *
 * @param {'education' | 'tap' | 'admin' | 'personal'} targetApp
 */
async function redirectToApp(targetApp) {
    showLoader.set('Redirecting...');
    try {
        const resolveUrl = APP_URLS[targetApp];
        if (!resolveUrl) {
            throw new Error(`Unknown target app: ${targetApp}`);
        }
        const targetUrl = resolveUrl();
        if (!targetUrl) {
            throw new Error(`Missing URL env var for target app: ${targetApp}`);
        }

        const token = await generateAppToken(targetApp);

        // If returnTo points to this target app's origin, preserve the
        // deep-link path + query. Otherwise discard and fall back to root.
        let destination = `${targetUrl}?token=${token}`;
        const returnTo = readReturnTo();
        if (returnTo) {
            clearReturnTo();
            try {
                const returnToUrl = new URL(returnTo);
                const targetOrigin = new URL(targetUrl).origin;
                if (returnToUrl.origin === targetOrigin) {
                    returnToUrl.searchParams.set('token', token);
                    destination = returnToUrl.toString();
                }
            } catch { /* fall through to default destination */ }
        }

        window.location.href = destination;
    } catch (error) {
        console.error('Failed to redirect to app:', error);
        Sentry.captureException(error);
        showLoader.set(false);
        throw error;
    }
}

/**
 * Sign out with token revocation across all apps.
 */
async function signOut() {
    postHogCapture('sign_out');

    // Revoke all refresh tokens server-side, but cap how long sign-out waits
    // on it so a cold-started/hung function can't freeze the UI — the revoke
    // still runs to completion in the background.
    await revokeTokensWithTimeout(callable);

    currentUser.set(null);
    classJoinCode.set(null);
    posthog.reset();

    await auth.signOut();
    await goto('/login');
}

export {
    signInWithEmail,
    signUpWithCode,
    signInWithClassLink,
    signInWithClever,
    resetPasswordEmail,
    getCurrentUser,
    signOut,
    getRedirectResultOAuthProvider,
    joinClassWithCode,
    joinTapWithCode,
    loginWithCode,
    changePassword,
    validateSignUpCode,
    redeemTapCodeForUser,
    generateAppToken,
    redirectToApp,
    getUserApps,
    inferTargetAppFromUrl,
    readReturnTo,
    clearReturnTo,
    setPendingTapCode,
    readPendingTapCode,
    clearPendingTapCode,
};
